import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  IconButton,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Close, CloudUpload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiGetFile } from "../../services/genesisApi/httpsMethods";
import { createExtraction } from "../../services/extractionApi";
import {
  describeUploadFailures,
  EXTRACTION_FILE_EXTENSIONS,
  getUploadErrorMessage,
  mergeSelectedFiles,
  partitionFilesByExtension,
  PDF_ONLY_EXTENSIONS,
  TENANCY_TEMPLATE_FILE_NAME,
  type FileUploadFailure,
} from "./fileExtractorUtils";
import { CustomButton } from "./customButton";
import { renderSubTitle } from "../renderComponents";
import { toNullableDate } from "../../pages/loanComps/loanCompPayload";
import { useReferenceLists } from "../../context/GlobalGetLists";
import type { Asset, Tenant, LoanComp } from "../../types/deal";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "./errorDialog";
import { excelCellToDate, toDateOnlyString } from "../../utils/formatDate";

type ExtractionMode = "default" | "term_sheet" | "initial_materials";

/** Extracted tenants may have their temporary id stripped before being handed back. */
export type ExtractedTenant = Omit<Tenant, "id"> & {
  id?: Tenant["id"];
  asset_name?: string;
};

interface ReusableFileExtractorProps {
  mode: "deal" | "comps";
  onAssetsExtracted?: (assets: Partial<Asset>[]) => void;
  onTenantsExtracted?: (tenants: ExtractedTenant[]) => void;
  onLoanCompsExtracted?: (loanComps: Partial<LoanComp>[]) => void;
  onDealExtracted?: (
    dealData: any,
    context?: { isTermSheet?: boolean; isInitialMaterials?: boolean },
  ) => void;
  existingAssets?: Partial<Asset>[];
  dealId?: string;
  useAsyncExtraction?: boolean; // If true, use async extraction and redirect
}

const ReusableFileExtractor = ({
  mode,
  onAssetsExtracted,
  onTenantsExtracted,
  onLoanCompsExtracted,
  onDealExtracted,
  existingAssets = [],
  dealId,
  useAsyncExtraction = true, // Default to async extraction
}: ReusableFileExtractorProps) => {
  const navigate = useNavigate();
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedAssetFile, setSelectedAssetFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const [condensedAssets, setCondensedAssets] = useState<any[]>([]);
  const [extractionMode, setExtractionMode] =
    useState<ExtractionMode>("default");
  const [dragActive, setDragActive] = useState(false);
  // Navigation to AI Extractions deferred until the user dismisses the
  // partial-failure summary (some files queued, some failed).
  const [pendingNavigation, setPendingNavigation] = useState<{
    path: string;
    state?: { duplicateMessage: string };
  } | null>(null);
  const isTermSheet = extractionMode === "term_sheet";
  const isInitialMaterials = extractionMode === "initial_materials";
  const isRestrictedExtractionMode = isTermSheet || isInitialMaterials;
  // Extract options for edit mode (when dealId is present)
  const [extractDeal, setExtractDeal] = useState(true);
  const [extractCommentary, setExtractCommentary] = useState(true);
  const [extractAssetsTenants, setExtractAssetsTenants] = useState(true);
  const [extractImages, setExtractImages] = useState(true);
  const { countries, sectors } = useReferenceLists();

  // Term sheet / initial materials: deal data only
  useEffect(() => {
    if (isRestrictedExtractionMode && dealId) {
      setExtractDeal(true);
      setExtractCommentary(false);
      setExtractAssetsTenants(false);
    }
  }, [isRestrictedExtractionMode, dealId]);

  useEffect(() => {
    if (!dealId) {
      setExtractionMode("default");
    }
  }, [dealId]);

  useEffect(() => {
    if (existingAssets?.length > 0) {
      const simplified = existingAssets.map((asset) => ({
        id: asset.id,
        asset_name: asset.asset_name,
        area_type: asset.area_type,
        currency: asset.currency,
      }));
      setCondensedAssets(simplified);
    }
  }, [existingAssets]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const assetFileInputRef = useRef<HTMLInputElement>(null);

  const acceptedExtensions = useMemo(
    () =>
      isInitialMaterials && dealId
        ? PDF_ONLY_EXTENSIONS
        : EXTRACTION_FILE_EXTENSIONS,
    [isInitialMaterials, dealId],
  );

  const showUnsupportedFiles = useCallback(
    (rejected: File[]) => {
      const names = rejected.map((file) => file.name).join(", ");
      const verb =
        rejected.length === 1
          ? "is not a supported file type"
          : "are not supported file types";
      showError(
        undefined,
        `${names} ${verb}. Supported types: ${acceptedExtensions.join(", ")}.`,
        "Unsupported file type",
      );
    },
    [acceptedExtensions, showError],
  );

  // Browse and drag-and-drop both validate the file type and append to the
  // current selection (files already selected are not added twice).
  const addFiles = useCallback(
    (files: File[]) => {
      const { accepted, rejected } = partitionFilesByExtension(
        files,
        acceptedExtensions,
      );
      if (accepted.length > 0) {
        setSelectedFiles((prev) => mergeSelectedFiles(prev, accepted));
      }
      if (rejected.length > 0) {
        showUnsupportedFiles(rejected);
      }
    },
    [acceptedExtensions, showUnsupportedFiles],
  );

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(Array.from(e.dataTransfer.files));
      }
    },
    [addFiles],
  );

  const handleDownloadTemplate = () => {
    const templateEndpoint = dealId
      ? `tenancy-schedule-template?deal_id=${dealId}`
      : "tenancy-schedule-template";
    apiGetFile(templateEndpoint)
      .then((response) => {
        if (response.success && response.data) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const a = document.createElement("a");
          a.href = url;
          a.download = TENANCY_TEMPLATE_FILE_NAME;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        } else {
          console.error("Failed to download template:", response.message);
        }
      })
      .catch((error) => {
        console.error("Error downloading template:", error);
      });
  };

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        addFiles(Array.from(files));
      }
      // Reset so selecting the same file again fires onChange.
      event.target.value = "";
    },
    [addFiles],
  );

  const handleAssetFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedAssetFile(file);
    }
    // Reset so re-selecting the same (e.g. edited) file fires onChange.
    event.target.value = "";
  };

  const handleExtract = async () => {
    if (selectedFiles.length === 0) return;

    // The extraction mode may have changed (e.g. to PDF-only initial
    // materials) after files were selected.
    const { rejected: unsupportedFiles } = partitionFilesByExtension(
      selectedFiles,
      acceptedExtensions,
    );
    if (unsupportedFiles.length > 0) {
      showUnsupportedFiles(unsupportedFiles);
      return;
    }

    setIsExtracting(true);
    try {
      // Use async extraction (new flow)
      if (useAsyncExtraction) {
        // Process each file individually
        const extractionIds: number[] = [];
        const failures: FileUploadFailure[] = [];
        let highlightedDuplicate = false;
        let duplicateMessage: string | undefined;

        for (const file of selectedFiles) {
          // When dealId is present (edit mode), use extract flags from checkboxes
          // Otherwise, extract everything (user selects what to use via "Inspect")
          let response: Awaited<ReturnType<typeof createExtraction>>;
          try {
            response = await createExtraction(file, {
              excel_provider: "anthropic_column_matching",
              is_term_sheet: isTermSheet,
              is_initial_materials: isInitialMaterials,
              assets:
                condensedAssets.length > 0
                  ? condensedAssets
                      .map((a) => a.asset_name || "")
                      .filter(Boolean)
                  : undefined,
              deal_id: dealId ? parseInt(dealId) : undefined,
              extract_deal: dealId
                ? isRestrictedExtractionMode
                  ? true
                  : extractDeal
                : undefined,
              extract_commentary: dealId
                ? isRestrictedExtractionMode
                  ? false
                  : extractCommentary
                : undefined,
              extract_assets_tenants: dealId
                ? isRestrictedExtractionMode
                  ? false
                  : extractAssetsTenants
                : undefined,
              extract_images: extractImages,
            });
          } catch (error) {
            console.error(`Error queuing extraction for ${file.name}:`, error);
            failures.push({
              fileName: file.name,
              message: getUploadErrorMessage(
                error instanceof Error ? error : undefined,
              ),
            });
            continue;
          }

          if (response && response.success && response.data) {
            const { id, duplicate, message } = response.data;
            extractionIds.push(id);
            if (extractionIds.length === 1 && duplicate) {
              highlightedDuplicate = true;
              duplicateMessage = message;
            }
          } else {
            failures.push({
              fileName: file.name,
              message: getUploadErrorMessage(response),
            });
          }
        }

        let navigation: typeof pendingNavigation = null;
        if (extractionIds.length > 0) {
          const params = new URLSearchParams({
            highlight: String(extractionIds[0]),
          });
          if (highlightedDuplicate) {
            params.set("duplicate", "true");
          }
          navigation = {
            path: `/ai-extractions?${params.toString()}`,
            state:
              highlightedDuplicate && duplicateMessage
                ? { duplicateMessage }
                : undefined,
          };
        }

        if (failures.length > 0) {
          // Don't navigate away while the failure summary is up: the user
          // would never see which files failed. When some files were queued,
          // navigate once the summary is dismissed; otherwise stay here with
          // the selection intact so the user can retry.
          setPendingNavigation(navigation);
          showError(
            undefined,
            describeUploadFailures(failures, extractionIds.length),
            "Some files could not be queued",
          );
        } else if (navigation) {
          navigate(navigation.path, { state: navigation.state });
        }
      } else {
        // Legacy synchronous extraction (for backwards compatibility)
        // This uses the old endpoint - keeping for now but should be deprecated
        const { apiFormPost } =
          await import("../../services/genesisApi/httpsMethods");

        for (const file of selectedFiles) {
          const formData = new FormData();
          formData.append("excel_provider", "anthropic_column_matching");
          formData.append("file", file);

          // Only extract loan_comps if in comps mode, otherwise extract everything
          if (mode === "comps") {
            formData.append("extract_loan_comps", "true");
          }
          formData.append("is_term_sheet", String(isTermSheet));
          formData.append("is_initial_materials", String(isInitialMaterials));

          if (condensedAssets.length > 0) {
            formData.append("assets", JSON.stringify(condensedAssets));
          }

          const response = await apiFormPost("ai-file-extraction", formData);

          if (response && response.success) {
            const responseData = response.data;
            if (onDealExtracted && responseData.deal_data) {
              onDealExtracted(responseData.deal_data, {
                isTermSheet,
                isInitialMaterials,
              });
            }

            if (onAssetsExtracted && responseData.asset_data) {
              onAssetsExtracted(responseData.asset_data);
            }

            if (onTenantsExtracted && responseData.asset_data) {
              // take each lease inside the assets object and add it to the tenants array
              const allTenants: Tenant[] = [];
              responseData.asset_data.forEach((asset: any) => {
                asset.tenants?.forEach((tenant: any) => {
                  // Add asset_name to each lease
                  const tenantWithAssetName = {
                    ...tenant,
                    asset_name: asset.asset_name,
                  };
                  allTenants.push(tenantWithAssetName);
                });
              });
              onTenantsExtracted(allTenants);
            }

            if (onLoanCompsExtracted && responseData.loan_comp_data) {
              onLoanCompsExtracted(responseData.loan_comp_data);
            }
          } else {
            showErrorFromResponse(response);
          }
        }
      }
    } catch (error) {
      console.error("Error extracting data:", error);
      showError(
        undefined,
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while extracting data.",
        "Extraction Error",
      );
    } finally {
      setIsExtracting(false);
    }
  };

  const handleAssetUpload = async () => {
    if (!selectedAssetFile) return;

    setIsUploadingAssets(true);
    try {
      // Read the Excel file
      const arrayBuffer = await selectedAssetFile.arrayBuffer();

      // Parse Excel file using SheetJS (xlsx) library
      const XLSX = await import("xlsx");
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      let assetsFound = false;
      let tenancyFound = false;
      let loanCompsFound = false;

      // Excel serials / Date / text (UK dd/mm/yyyy or ISO) → local-midnight Date, or null
      const convertExcelDate = (value: unknown): Date | null =>
        excelCellToDate(value);

      // Helper function to process asset data and convert dates
      const processAssetData = (asset: any): Partial<Asset> => {
        const processedAsset = { ...asset };

        // Convert Excel dates to proper Date objects
        if (processedAsset.valuation_date) {
          processedAsset.valuation_date = convertExcelDate(
            processedAsset.valuation_date,
          );
        }
        if (processedAsset.acquisition_date) {
          processedAsset.acquisition_date = convertExcelDate(
            processedAsset.acquisition_date,
          );
        }
        if (processedAsset.created_at) {
          processedAsset.created_at = convertExcelDate(
            processedAsset.created_at,
          );
        }
        if (processedAsset.updated_at) {
          processedAsset.updated_at = convertExcelDate(
            processedAsset.updated_at,
          );
        }

        // Match country value with reference list and flatten to string
        if (
          processedAsset.country &&
          typeof processedAsset.country === "string"
        ) {
          const matchingCountry = countries.find(
            (country: any) =>
              country.nicename.toLowerCase() ===
              processedAsset.country.toLowerCase(),
          );
          if (matchingCountry) {
            processedAsset.country = matchingCountry;
          }
        }

        if (
          processedAsset.deal_id &&
          typeof processedAsset.deal_id === "number"
        ) {
          processedAsset.deal = { id: processedAsset.deal_id };
          delete processedAsset.deal_id;
        }

        // Match sector value with reference list and flatten to string
        if (
          processedAsset.sector &&
          typeof processedAsset.sector === "string"
        ) {
          const matchingSector = sectors.find(
            (sector: any) =>
              sector.sector.toLowerCase() ===
              processedAsset.sector.toLowerCase(),
          );
          if (matchingSector) {
            processedAsset.sector = matchingSector;
          }
        }

        return processedAsset;
      };

      // Helper function to process tenant data and convert dates
      const processTenantData = (tenant: any): Tenant => {
        const processedTenant = { ...tenant } as Tenant;

        // Convert Excel dates to proper Date objects
        if (processedTenant.lease_start_date) {
          const date = convertExcelDate(processedTenant.lease_start_date);
          processedTenant.lease_start_date = date ? toDateOnlyString(date) : null;
        }
        if (processedTenant.lease_end_date) {
          const date = convertExcelDate(processedTenant.lease_end_date);
          processedTenant.lease_end_date = date ? toDateOnlyString(date) : null;
        }
        if (processedTenant.break_option_date) {
          const date = convertExcelDate(processedTenant.break_option_date);
          processedTenant.break_option_date = date ? toDateOnlyString(date) : null;
        }
        if (processedTenant.next_review_date) {
          const date = convertExcelDate(processedTenant.next_review_date);
          processedTenant.next_review_date = date ? toDateOnlyString(date) : null;
        }
        if (processedTenant.rent_free_end_date) {
          const date = convertExcelDate(processedTenant.rent_free_end_date);
          processedTenant.rent_free_end_date = date ? toDateOnlyString(date) : null;
        }

        return processedTenant;
      };

      // Helper function to process loan comp data into the backend loan comp shape
      // (single `country` / `sector` objects, `date` as YYYY-MM-DD). Unknown sheet
      // columns are kept for display but dropped by buildLoanCompPayload on upload.
      const firstSheetText = (
        row: Record<string, unknown>,
        keys: string[],
      ): string | null => {
        for (const key of keys) {
          const value = row[key];
          if (typeof value === "string" && value.trim() !== "") {
            return value.trim();
          }
        }
        return null;
      };

      const processLoanCompData = (loanComp: any): Partial<LoanComp> => {
        const processedLoanComp = { ...loanComp };

        if (processedLoanComp.date !== undefined) {
          processedLoanComp.date = toNullableDate(
            convertExcelDate(processedLoanComp.date),
          );
        }

        // Accept a single "country" / "sector" column, or the first of the legacy
        // country_1..country_4 / sector_1..sector_4 columns (the backend stores one).
        const countryName = firstSheetText(processedLoanComp, [
          "country",
          "country_1",
          "country_2",
          "country_3",
          "country_4",
        ]);
        const sectorName = firstSheetText(processedLoanComp, [
          "sector",
          "sector_1",
          "sector_2",
          "sector_3",
          "sector_4",
        ]);
        for (let i = 1; i <= 4; i++) {
          delete processedLoanComp[`country_${i}`];
          delete processedLoanComp[`sector_${i}`];
        }

        if (countryName) {
          const matchingCountry = countries.find(
            (c: any) => c.nicename?.toLowerCase() === countryName.toLowerCase(),
          );
          processedLoanComp.country = matchingCountry
            ? { id: matchingCountry.id, nicename: matchingCountry.nicename }
            : { id: 0, nicename: countryName };
        } else {
          processedLoanComp.country = null;
        }

        if (sectorName) {
          const matchingSector = sectors.find(
            (s: any) => s.sector?.toLowerCase() === sectorName.toLowerCase(),
          );
          processedLoanComp.sector = matchingSector
            ? { id: matchingSector.id, sector: matchingSector.sector }
            : { id: 0, sector: sectorName };
        } else {
          processedLoanComp.sector = null;
        }

        return processedLoanComp;
      };

      // Process Assets sheet
      const assetsSheet = workbook.Sheets["Assets"];
      if (assetsSheet) {
        const rawAssetsData = XLSX.utils.sheet_to_json(assetsSheet);

        const processedAssetsData = rawAssetsData.map(
          processAssetData,
        ) as Partial<Asset>[];

        // Ensure each imported asset has a unique temporary string id to avoid collisions
        processedAssetsData.forEach((asset: any, index: number) => {
          if (!asset.id || typeof asset.id !== "number") {
            asset.id = `tmp-asset-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
          }
        });

        // Add dealId to all assets if available
        if (dealId) {
          processedAssetsData.forEach((asset) => {
            (asset as any).deal = { id: parseInt(dealId) };
          });
        }

        // Get the Tenancy Schedule sheet and merge with assets
        const tenancySheet = workbook.Sheets["Tenants"];
        if (tenancySheet) {
          const rawTenancyData = XLSX.utils.sheet_to_json(tenancySheet);

          let processedTenancyData = rawTenancyData.map(
            processTenantData,
          ) as ExtractedTenant[];

          let idsChanged = false;

          processedTenancyData.forEach((tenant: any, index: number) => {
            if (!tenant.id || typeof tenant.id !== "number") {
              idsChanged = true;
              tenant.id = `tmp-tenant-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
            }
          });

          // Match tenants to their corresponding assets based on asset_name
          if (
            processedTenancyData.length > 0 &&
            processedAssetsData.length > 0
          ) {
            // Initialize tenants array for each asset
            processedAssetsData.forEach((asset) => {
              asset.tenants = [];
            });

            // Match tenants to assets by asset_name
            const tenantsUpdated = structuredClone(processedTenancyData);
            processedTenancyData = tenantsUpdated.map((tenant) => {
              const matchingAsset = processedAssetsData.find((asset) => {
                if (asset.id && tenant.asset_id) {
                  return (
                    asset.id.toString().toLowerCase().trim() ===
                    tenant.asset_id.toString().toLowerCase().trim()
                  );
                } else if (asset.asset_name && tenant.asset_name) {
                  return (
                    asset.asset_name.toString().toLowerCase().trim() ===
                    tenant.asset_name.toString().toLowerCase().trim()
                  );
                }
                return false;
              });

              if (matchingAsset) {
                // Add tenant to the matching asset's tenants array
                if (!matchingAsset.tenants) {
                  matchingAsset.tenants = [];
                }
                // remove the temporary id before pushing to assets
                const tenantsUpdate = { ...tenant };
                tenantsUpdate.asset_name = matchingAsset?.asset_name;
                if (idsChanged && tenantsUpdate.id) {
                  delete tenantsUpdate.id;
                }
                matchingAsset.tenants.push(tenantsUpdate as any);
                return tenantsUpdate;
                // add tenant to the matching asset's tenants array
              } else {
                // Still add the tenant to the context, but without asset association
                if (processedAssetsData.length > 0) {
                  if (!processedAssetsData[0].tenants) {
                    processedAssetsData[0].tenants = [];
                  }
                  const { id: _tempId, ...tenantWithoutId } = tenant;
                  processedAssetsData[0].tenants.push(tenantWithoutId as any);
                }

                return tenant;
              }
            });
          }

          // Use callback to add tenants
          if (onTenantsExtracted) {
            onTenantsExtracted(processedTenancyData);
          }

          // Clean up temporary negative IDs from tenants while keeping tenant associations
          processedAssetsData.forEach((asset) => {
            if (asset.tenants) {
              asset.tenants.forEach((tenant) => {
                // Remove temporary negative asset_id if it exists
                if (tenant.asset_id && tenant.asset_id < 0) {
                  (tenant as any).asset_id = undefined;
                }
              });
            }
          });

          tenancyFound = true;
        } else {
          console.warn("Tenancy Schedule sheet not found in Excel file");
        }

        if (onAssetsExtracted) {
          onAssetsExtracted(processedAssetsData);
        }
        assetsFound = true;
      } else {
        console.warn("Assets sheet not found in Excel file");
      }

      // Process Loan Comps sheet
      const loanCompsSheet = workbook.Sheets["Loan Comps"];
      if (loanCompsSheet) {
        const rawLoanCompsData = XLSX.utils.sheet_to_json(loanCompsSheet);
        const processedLoanCompsData = rawLoanCompsData.map(
          processLoanCompData,
        ) as Partial<LoanComp>[];

        if (onLoanCompsExtracted) {
          onLoanCompsExtracted(processedLoanCompsData);
        }
        loanCompsFound = true;
      } else {
        console.warn("Loan Comps sheet not found in Excel file");
      }

      // Provide user feedback
      if (!assetsFound && !tenancyFound && !loanCompsFound) {
        alert(
          'No valid sheets found. Please ensure your Excel file contains "Assets", "Tenancy Schedule", and/or "Loan Comps" sheets.',
        );
      } else {
        const foundSheets = [];
        if (assetsFound) foundSheets.push("Assets");
        if (tenancyFound) foundSheets.push("Tenancy Schedule");
        if (loanCompsFound) foundSheets.push("Loan Comps");
        alert(`Successfully parsed: ${foundSheets.join(", ")}`);
      }
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      alert(
        "Error parsing Excel file. Please ensure it's a valid Excel file with the correct sheet names.",
      );
    } finally {
      setIsUploadingAssets(false);
    }
  };

  const triggerAssetFileInput = () => {
    assetFileInputRef.current?.click();
  };

  const handleCloseErrorDialog = () => {
    hideError();
    if (pendingNavigation) {
      const { path, state } = pendingNavigation;
      setPendingNavigation(null);
      navigate(path, { state });
    }
  };

  const cardShellSx = {
    p: 3,
    height: "100%",
    borderRadius: "12px",
    border: "1px solid #e5e7eb",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
  } as const;

  return (
    <Box
      sx={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: "50px" }}
    >
      {errorDialog.open && (
        <ErrorDialog
          open={errorDialog.open}
          onClose={handleCloseErrorDialog}
          closeText={pendingNavigation ? "Continue to AI Extractions" : undefined}
          apiResponse={errorDialog.apiResponse}
          customMessage={errorDialog.customMessage}
          title={errorDialog.title}
        />
      )}

      {/* AI File Extraction Section */}
      <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
        <Paper
          elevation={0}
          sx={{
            ...cardShellSx,
            border: dragActive ? "2px dashed #1976d2" : cardShellSx.border,
            backgroundColor: dragActive ? "#f0f7ff" : "white",
            transition: "all 0.2s ease",
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {renderSubTitle("Extract Information with AI")}

          {/* Drag & Drop Zone */}
          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              marginTop: 1,
              padding: 3,
              textAlign: "center",
              cursor: "pointer",
              mb: 2,
              backgroundColor: dragActive ? "#e3f2fd" : "#fafafa",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#1976d2",
              },
              transition: "all 0.2s ease",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload
              sx={{
                fontSize: 48,
                color: dragActive ? "#1976d2" : "#999",
                mb: 1,
              }}
            />
            <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {dragActive ? "Drop files here" : "Drag & drop files here"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              or click to browse
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {isInitialMaterials && dealId
                ? "Supported format: PDF only"
                : "Supported formats: PDF, XLSX, XLS, PNG, MSG, EML (Outlook emails)"}
            </Typography>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
            multiple
            accept={acceptedExtensions.join(",")}
          />

          {dealId && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", mb: 1, color: "#55575C" }}
              >
                Extraction mode
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={extractionMode}
                onChange={(_event, value: ExtractionMode | null) => {
                  if (value) {
                    setExtractionMode(value);
                  }
                }}
                sx={{ flexWrap: "wrap" }}
              >
                <ToggleButton value="default">Default</ToggleButton>
                <ToggleButton value="term_sheet">Term sheet</ToggleButton>
                <ToggleButton value="initial_materials">
                  Initial materials
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Box
            sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                fontSize: "14px",
                color: "#55575C",
              }}
            >
              <input
                type="checkbox"
                checked={extractImages}
                onChange={(e) => setExtractImages(e.target.checked)}
                style={{ marginRight: "8px", marginTop: "3px" }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "#55575C" }}
                >
                  Extract marketing images
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Extracts photos from PDF, MSG, or EML files
                </Typography>
              </Box>
            </label>
          </Box>

          {dealId && extractionMode === "default" && (
            <Box
              sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", mb: 1, color: "#55575C" }}
              >
                Extract Options:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    color: "#55575C",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={extractDeal}
                    onChange={(e) => setExtractDeal(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  Deal Data
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    color: "#55575C",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={extractCommentary}
                    onChange={(e) => setExtractCommentary(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  Commentary
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    color: "#55575C",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={extractAssetsTenants}
                    onChange={(e) => setExtractAssetsTenants(e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  Assets/Tenants
                </label>
              </Box>
            </Box>
          )}

          {dealId && isTermSheet && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 2,
                color: "#999",
                fontStyle: "italic",
              }}
            >
              Term sheets only extract deal data (PDF or other supported files).
            </Typography>
          )}

          {dealId && isInitialMaterials && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 2,
                color: "#999",
                fontStyle: "italic",
              }}
            >
              Initial materials populate loan sizing, fees, covenants, and
              related deal fields only. Commentary, assets, and comps are not
              extracted.
            </Typography>
          )}

          {selectedFiles.length > 0 && (
            <>
              <Box sx={{ mb: 2 }}>
                {selectedFiles.map((file, index) => (
                  <Box
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#55575C",
                        fontWeight: "bold",
                        minWidth: 0,
                        wordBreak: "break-word",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Tooltip title="Remove file">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${file.name}`}
                          disabled={isExtracting}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
              </Box>

              <CustomButton
                text={isExtracting ? "Extracting..." : "Extract"}
                handleButtonPress={handleExtract}
                isButtonDisabled={isExtracting}
                solid={true}
                widthOverride="150px"
              />
            </>
          )}
        </Paper>
      </Box>

      <Box sx={{ flex: "1 1 400px", minWidth: "400px" }}>
        <Paper elevation={0} sx={cardShellSx}>
          {renderSubTitle("Upload Excel Template")}
          <Typography sx={{ fontSize: "14px", color: "#55575C", mb: 2 }}>
            Important: If you have updated a deal you must always download a new
            excel template rather than trying to re-upload an outdated one.
          </Typography>

          <input
            ref={assetFileInputRef}
            type="file"
            hidden
            onChange={handleAssetFileChange}
            accept=".xlsx,.xls"
          />

          <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
            <CustomButton
              text="Choose Excel File"
              handleButtonPress={triggerAssetFileInput}
              isButtonDisabled={false}
              solid={false}
              widthOverride="150px"
            />

            <CustomButton
              text="Download Template"
              handleButtonPress={handleDownloadTemplate}
              isButtonDisabled={false}
              solid={true}
              widthOverride="150px"
            />
          </Box>

          {selectedAssetFile && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: "14px", color: "#55575C" }}>
                  {selectedAssetFile.name}
                </Typography>
              </Box>

              <CustomButton
                text={isUploadingAssets ? "Parsing..." : "Parse Excel File"}
                handleButtonPress={handleAssetUpload}
                isButtonDisabled={isUploadingAssets}
                solid={true}
                widthOverride="150px"
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ReusableFileExtractor;
